<template>
  <div
    id="login-modal"
    scroll="keep"
    class="modal-card"
    @close="$emit('close')"
  >
    <div
      v-if="!isShowVerifyModal"
      class="is-expanded"
    >
      <vue-recaptcha
        ref="recaptcha"
        :sitekey="siteKey"
        size="invisible"
        @verify="loginWithEmail"
        @expired="onCaptchaExpired"
      />
      <div class="modal-header">
        {{ $t('buttons.login') }}
      </div>
      <div class="is-expanded modal-content">
        <div class="or-splitter">
          {{ $t('register.or') }}
        </div>
        <div class="modal-content-left pd-right-55">
          <FacebookLoginButton
            :text="$t('register.login_with', ['Facebook'])"
            @onSignInSuccess="onSignInWithFacebook"
          />
          <GoogleLoginButton
            :text="$t('register.login_with', ['Google'])"
            @onSignInSuccess="onSignInWithGoogle"
          />
          <!-- <div class="license-wrapper">
            <p class="license-text"> {{ $t('register.license_by') }}</p>
            <img src="/static/svg/ministry-of-finance-logo.svg">
            <img src="/static/svg/sec-logo.svg">
          </div> -->
        </div>
        <div class="modal-content-right pd-left-55">
          <b-field
            id="login-field"
            class="input-box"
          >
            <b-input
              v-validate="'required|email'"
              v-model="email"
              :disabled="isLoading"
              :placeholder="$t('register.email_placeholder')"
              class="is-underline-input-white is-underline-input-gray"
              name="email"
              type="text"
              autocomplete="off"
              data-test="login-modal--email-field"
              @keyup.enter.native="executeRecaptcha()"
              @blur="trackInputForm({ key: 'email', name: 'email'})"
            />
            <span class="is-invalid">{{ errors.first('email') }}</span>
            <img
              src="/static/svg/login-icon-email.svg"
              class="ic-email-in-input"
            >
          </b-field>
          <b-field
            id="login-field"
            class="input-box"
          >
            <b-input
              v-validate="'required'"
              v-model="password"
              :disabled="isLoading"
              :placeholder="$t('register.password_placeholder')"
              class="is-underline-input-white is-underline-input-gray"
              name="password"
              type="password"
              data-test="login-modal--password-field"
              @keyup.enter.native="executeRecaptcha()"
              @blur="trackInputForm({ key: 'password', name: 'password'})"
            />
            <span class="is-invalid">{{ errors.first('password') }}</span>
            <img
              src="/static/svg/login-icon-password.svg"
              class="ic-password-in-input"
            >
          </b-field>
          <b-checkbox
            v-model="isRememberMe"
            :disabled="isLoading"
            class="check-box"
            data-test="login-modal--rememberMe-checkbox"
          >
            {{ $t('login.remember_me') }}
          </b-checkbox>
          <b-button
            :loading="isLoading"
            expanded
            class="normal-login-btn"
            data-test="login-modal--login-btn"
            @click="executeRecaptcha()"
          >
            {{ $t('login.login_uppercase') }}
          </b-button>
          <div class="is-expanded">
            <div
              class="modal-content-left register-text-box"
              data-test="login-modal--register-btn"
              @click="linkToRegisterPage()"
            >
              {{ $t('login.register_now') }}
            </div>
            <div
              class="modal-content-right forgot-password-text-box"
              @click="linkToForgotPasswordPage()"
            >
              {{ $t('forgot_password.forgot_password') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <Verify2FAModal
        ref="verify2FAModal"
        @submit="onSubmitVerify2FA"
      />
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import FacebookLoginButton from '@/components/FacebookLoginButton'
import GoogleLoginButton from '@/components/GoogleLoginButton'
import Verify2FAModal from '@/components/Verify2FAModal'
import RequestEmailModal from '@/components/RequestEmailModal'
import Service from './service.js'
import AmplitudeService from '@/services/amplitude'
import { kulapApi } from '@/resources'
import { showToastError } from '@/utils/helper'
import { ENV, RECAPTCHA_SITE_KEY } from '@/constants'

export default {
  name: 'LoginModal',
  components: {
    FacebookLoginButton,
    GoogleLoginButton,
    Verify2FAModal,
    VueRecaptcha
  },
  data () {
    return {
      email: '',
      password: '',
      isRememberMe: true,
      service: null,
      twoFAData: {},
      isShowVerifyModal: false,
      isLoading: false,
      siteKey: RECAPTCHA_SITE_KEY
    }
  },
  created () {
    this.service = new Service({ store: this.$store, kulapApi })
  },
  methods: {
    async executeRecaptcha () {
      try {
        const validateFieldResult = await this.$validator.validate()
        if (validateFieldResult) {
          if (!window.Cypress) {
            this.isLoading = true
            this.$refs.recaptcha.execute()
          } else {
            this.loginWithEmail()
          }
        }
      } catch (e) {
        showToastError(e)
      }
    },
    onCaptchaExpired () {
      this.$refs.recaptcha.reset()
      this.isLoading = false
    },
    async onSignInWithFacebook (facebookResponse) {
      try {
        const loginData = await this.service.loginWithFacebook(facebookResponse.authResponse.userID, facebookResponse.authResponse.accessToken, this.isRememberMe)
        if (loginData && loginData.requestEmail) {
          this.$emit('close')
          this.openRequestEmailModal()
        } else if (loginData && loginData.secretKeyForVerify2FA) {
          this.twoFAData = loginData
          this.openVerify2FAModal()
        } else {
          this.$emit('loginSuccess', loginData)
          this.$emit('close')
        }
      } catch (e) {
        showToastError(e)
      }
    },
    async onSignInWithGoogle (googleUser) {
      try {
        const loginData = await this.service.loginWithGoogle(googleUser, this.isRememberMe)
        if (loginData && loginData.secretKeyForVerify2FA) {
          this.twoFAData = loginData
          this.openVerify2FAModal()
        } else {
          this.$emit('loginSuccess', loginData)
          this.$emit('close')
        }
      } catch (e) {
        showToastError(e)
      }
    },
    openRequestEmailModal () {
      let onRequestEmailModal = this.$buefy.modal.open({
        component: RequestEmailModal,
        hasModalCard: true,
        parent: this,
        events: {
          getEmail: async (email, recaptchaToken) => {
            try {
              await this.service.updateUserEmail(email, recaptchaToken)
              onRequestEmailModal.close()
              this.$router.push({name: 'RegisterStatus', query: {opts: 'facebookNoEmail'}})
            } catch (e) {
              showToastError(e)
            }
          }
        },
        canCancel: ['escape', 'outside', 'x']
      })
    },
    async loginWithEmail (recaptchaToken) {
      this.isLoading = true
      try {
        if (window.Cypress && ['develop', 'development', 'alpha'].includes(ENV)) {
          recaptchaToken = window.Cypress.env().RECAPTCHA_TEST_TOKEN
        }
        const loginData = await this.service.loginWithEmailPassword(this.email, this.password, this.isRememberMe, recaptchaToken)
        if (loginData && loginData.secretKeyForVerify2FA) {
          this.twoFAData = loginData
          this.openVerify2FAModal()
        } else {
          this.$emit('loginSuccess', loginData)
          this.$emit('close')
        }
      } catch (e) {
        showToastError(e)
      }
      this.$refs.recaptcha.reset()
      this.isLoading = false
    },
    openVerify2FAModal () {
      this.isShowVerifyModal = true
    },
    async onSubmitVerify2FA (code) {
      try {
        const result = await this.service.verify2FACode(code, this.twoFAData.secretKeyForVerify2FA, this.twoFAData.userId)
        if (Object.keys(result).length) {
          this.service.setUserAuthCookies(result, this.isRememberMe)
          this.$emit('loginSuccess', result)
          this.$emit('close')
        }
      } catch (e) {
        this.$refs.verify2FAModal.updateIsLoading(false)
        showToastError(e)
      }
    },
    linkToRegisterPage () {
      this.$router.push({ name: 'Register' })
      this.$emit('close')
    },
    linkToForgotPasswordPage () {
      this.$router.push({ name: 'ForgotPassword' })
      this.$emit('close')
    },
    trackInputForm ({ key, name }) {
      if (this[key]) {
        const isValid = this.fields[name] && this.fields[name].valid
        AmplitudeService.inputField(key, isValid, 'EVENT_LOGIN_FLOW')
      }
    }
  }
}
</script>

<style>
#login-modal .modal-content {
  overflow: hidden;
}
#login-field div {
  float: left;
  width: 100%;
}
#login-field div input {
  padding-left: 35px;
}
</style>

<style lang="scss" scoped>
.modal-card {
  background-color: #fff;
  border-radius: 5px;
  padding: 30px 40px;
  min-width: 800px;
}
.modal-header {
  margin-bottom: 70px;
  font-size: 25px;
  font-weight: bold;
  color: #1e1e1e;
  text-align: center;
}
.modal-content {
  position: relative;
}
.pd-left-55 {
  padding-left: 55px;
}
.pd-right-55 {
  padding-right: 55px;
}
.dp-content {
  display: contents;
}
.modal-content-left {
  float: left;
  width: 50%;
}
.modal-content-right {
  float: left;
  width: calc(50% - 1px);
  border-left: 1px solid #d5d5d5;
}
.or-splitter {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-top: -30px;
  margin-left: -28px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #4d4d4d;
  background-color: #fff;
  padding: 16px;
}
.input-box {
  flex-direction: column;
  margin-bottom: 20px;
  position: relative;
}
.check-box {
  margin-bottom: 20px;
}
.ic-email-in-input {
  position: absolute;
  left: 3px;
  top: 9px
}
.ic-password-in-input {
  position: absolute;
  left: 9px;
  top: 9px
}
.normal-login-btn {
  display: flex;
  background-color: #2193ff;
  color: #fff;
  padding: 20px 13px;
  text-align: center;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.4px;
  border-radius: 5px;
  border: unset;
  margin-bottom: 22px;
  cursor: pointer;
}
.register-text-box {
  font-weight: 500;
  letter-spacing: 0.4px;
  color: #2193ff;
  text-align: right;
  padding-right: 20px;
  cursor: pointer;
}
.register-text-box:hover {
  opacity: 0.7;
}
.forgot-password-text-box {
  letter-spacing: 0.5px;
  color: #939393;
  padding-left: 20px;
  cursor: pointer;
}
.license-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-around;
  p {
    font-size: 15px;
    font-weight: 600;
  }
}
.forgot-password-text-box:hover {
  opacity: 0.7;
}
/* Less than tablet */
@media only screen and (max-width: 1087px) {
  #login-modal {
    width: 90vw;
    min-width: 0;
    margin: 0 auto;
  }
}
/* Mobile */
@media only screen and (max-width: 768px) {
  .modal-card {
    padding: 7%;
  }
  .modal-header {
    margin-bottom: 7%;
  }
  .or-splitter {
    display: none;
  }
  .license-wrapper {
    display: none;
  }
  .modal-content {
    margin: 0;
  }
  .modal-content-left {
    width: 100%;
    padding: 0;
  }
  .modal-content-right {
    width: 100%;
    padding: 7% 0 0 0;
    border-top: 1px solid #d5d5d5;
    border-left: none;
  }
  .input-box {
    margin-bottom: 7%;
  }
  .check-box {
    margin-bottom: 7%;
  }
  .normal-login-btn {
    margin-bottom: 10px;
  }
  .register-text-box {
    width: 50%;
    text-align: center;
  }
  .forgot-password-text-box {
    width: 50%;
    text-align: center;
    border: 0;
    padding: 0;
  }
  #login-modal {
    margin-top: 7%;
  }
}

/* iPhone 5/SE */
@media only screen and (max-width: 320px) {
  #login-modal {
    margin-top: calc(7% + 10px);
  }
}
</style>



// WEBPACK FOOTER //
// src/features/LoginModal/index.vue